import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Star, Pencil, Camera, Bell, Trash2, ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { istNow } from "../lib/dates";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (p) => (p ? `${BACKEND}${p}` : "");

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const dutyDaily = (s, e) => {
  if (!s || !e || !String(s).includes(":") || !String(e).includes(":")) return 0;
  const m = (t) => { const [h, mn] = t.split(":").map(Number); return h * 60 + mn; };
  let d = m(e) - m(s); if (d < 0) d += 1440; return d;
};
const monthInfo = (form) => {
  const now = istNow();
  const y = now.getFullYear(), mo = now.getMonth();
  const days = new Date(y, mo + 1, 0).getDate();
  const target = WEEKDAYS.indexOf(form.weekly_off);            // Mon=0..Sun=6
  let offs = 0;
  if (target >= 0) for (let d = 1; d <= days; d++) if (new Date(y, mo, d).getDay() === ((target + 1) % 7)) offs++;
  const daily = dutyDaily(form.work_start, form.work_end);
  const workingDays = Math.max(days - offs, 0);
  return { daily, days, offs, workingDays, stdMin: daily * workingDays };
};

// Move / Transfer control on each staff card — Temporary (keeps home branch) or Permanent
// (destination becomes the new home/default branch). No design change; only adds the two options.
function StaffTransfer({ s, branches, branchName, onTemp, onPerm, onHome }) {
  const [dest, setDest] = useState(s.branch_id);
  useEffect(() => { setDest(s.branch_id); }, [s.branch_id]);
  const canTemp = dest && dest !== s.branch_id;
  const canPerm = dest && dest !== s.home_branch_id;
  return (
    <div className="space-y-2" data-testid={`staff-transfer-${s.id}`}>
      <Label className="text-[11px] text-muted-foreground">Move / Transfer (Home: {branchName(s.home_branch_id)})</Label>
      <Select value={dest} onValueChange={setDest}>
        <SelectTrigger className="bg-secondary h-8" data-testid={`transfer-branch-${s.id}`}><SelectValue placeholder="Select destination branch" /></SelectTrigger>
        <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
      </Select>
      <div className="grid grid-cols-2 gap-2">
        <Button size="sm" variant="outline" className="h-8 rounded-sm" disabled={!canTemp} onClick={() => onTemp(s, dest)} data-testid={`temp-transfer-${s.id}`}>Temporary</Button>
        <Button size="sm" variant="outline" className="h-8 rounded-sm border-primary/50 text-primary" disabled={!canPerm} onClick={() => onPerm(s, dest)} data-testid={`perm-transfer-${s.id}`}>Permanent</Button>
      </div>
      {s.is_away && <Button size="sm" variant="outline" className="w-full h-8 rounded-sm border-amber-600/50 text-amber-300" onClick={() => onHome(s)} data-testid={`move-home-${s.id}`}>Move back to Home branch</Button>}
      <p className="text-[10px] text-muted-foreground leading-snug">Temporary keeps home as {branchName(s.home_branch_id)} — business counts at the branch where the service is performed. Permanent makes the selected branch the new home/default.</p>
    </div>
  );
}


const EMPTY = {
  name: "", designation: "Stylist", incentive_designation: "", branch_id: "", specialization: "",
  mobile: "", whatsapp: "", gender: "", joining_date: "", weekly_off: "Monday",
  monthly_salary: 30000, standard_monthly_minutes: 15600, overtime_rate: "",
  incentive_eligible: true, biometric_id: "", photo: "", services: [], status: "active",
  show_in_public: true,
  experience: "", work_start: "", work_end: "", whatsapp_notify: true, notify_prefs: {}, daily_report_enabled: false,
};

export default function Staff() {
  const { branches, selected, branchParam } = useBranch();
  const { user } = useAuth();
  const isOwner = ["OWNER"].includes(user?.role);
  const isSuper = ["OWNER", "SUPER_ADMIN"].includes(user?.role);
  const isReception = user?.role === "RECEPTION";
  const uperms = user?.permissions || {};
  const canAdd = !isReception && (isSuper || !!uperms.add_staff);
  const canEdit = !isReception && (isSuper || !!uperms.edit_staff);
  const canSeeFull = !isReception && (isSuper || !!uperms.staff_view);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [delReason, setDelReason] = useState("");
  const [awayOnly, setAwayOnly] = useState(false);
  const [incDesigs, setIncDesigs] = useState([]);
  const [allServices, setAllServices] = useState([]);
  useEffect(() => { api.get("/incentive-settings/designations").then((r) => setIncDesigs(r.data.designations || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/services").then((r) => setAllServices(r.data || [])).catch(() => {}); }, []);
  const toggleSvc = (id) => setForm((f) => { const set = new Set(f.services || []); set.has(id) ? set.delete(id) : set.add(id); return { ...f, services: [...set] }; });
  const toggleCat = (ids, allOn) => setForm((f) => { const set = new Set(f.services || []); ids.forEach((id) => allOn ? set.delete(id) : set.add(id)); return { ...f, services: [...set] }; });
  const [awayInfo, setAwayInfo] = useState({});
  const [showRemoved, setShowRemoved] = useState(false);
  const [removed, setRemoved] = useState([]);
  const [bulkSel, setBulkSel] = useState(new Set());
  const bulkVisibility = async (show) => {
    try {
      await api.post("/staff/bulk-visibility", { ids: [...bulkSel], show_in_public: show });
      toast.success(`${bulkSel.size} staff ${show ? "shown in" : "hidden from"} online booking`);
      setBulkSel(new Set()); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const load = () => api.get("/staff", { params: { branch_id: branchParam } }).then((r) => setStaff(r.data));
  const loadRemoved = () => api.get("/staff/removed").then((r) => setRemoved(r.data)).catch(() => setRemoved([]));
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); if (isOwner) loadRemoved(); }, [branchParam]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editId) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/staff/${editId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo: data.photo })); toast.success("Photo updated"); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openNew = () => { setForm({ ...EMPTY, branch_id: (selected && selected !== "all") ? selected : (branches[0]?.id || "") }); setEditId(null); setOpen(true); };
  const openEdit = async (s) => {
    let full = s;
    try { const { data } = await api.get(`/staff/${s.id}`); full = data; } catch { /* keep list data */ }
    setForm({ ...EMPTY, ...full, overtime_rate: full.overtime_rate ?? "" }); setEditId(s.id); setOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/staff/${delTarget.id}`, { params: { reason: delReason } });
      toast.success(`${delTarget.name} removed`); setDelTarget(null); setDelReason(""); load(); loadRemoved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const restore = async (s) => {
    try {
      await api.put(`/staff/${s.id}/restore`);
      toast.success(`${s.name} reactivated`); load(); loadRemoved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const moveBranch = async (s, branch_id) => {
    if (branch_id === s.branch_id) return;
    try { await api.put(`/staff/${s.id}/branch`, { branch_id }); toast.success(`${s.name} moved to ${branchName(branch_id)}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const moveHome = async (s) => {
    try { await api.put(`/staff/${s.id}/move-home`); toast.success(`${s.name} moved back to home branch (${branchName(s.home_branch_id)})`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const permanentTransfer = async (s, branch_id) => {
    if (!branch_id || branch_id === s.home_branch_id) return;
    if (!window.confirm(`Permanently transfer ${s.name} to ${branchName(branch_id)}? This becomes their new home/default branch. Existing salary, attendance, incentives & business history stay intact.`)) return;
    try { await api.put(`/staff/${s.id}/permanent-branch`, { branch_id }); toast.success(`${s.name} permanently transferred to ${branchName(branch_id)}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => {
    staff.filter((s) => s.is_away && !awayInfo[s.id]).forEach((s) => {
      api.get(`/staff/${s.id}/away-earnings`).then((r) => setAwayInfo((m) => ({ ...m, [s.id]: r.data }))).catch(() => {});
    });
  }, [staff]); // eslint-disable-line react-hooks/exhaustive-deps

  const dutyInvalid = Boolean(form.work_start && form.work_end && form.work_end <= form.work_start);
  const [dupMsg, setDupMsg] = useState("");
  useEffect(() => {
    if (!open) { setDupMsg(""); return; }
    const m = (form.mobile || "").replace(/\D/g, ""), w = (form.whatsapp || "").replace(/\D/g, "");
    if (m.length < 10 && w.length < 10) { setDupMsg(""); return; }
    let live = true;
    api.get("/staff/check-number", { params: { mobile: form.mobile, whatsapp: form.whatsapp, exclude_id: editId || undefined } })
      .then((r) => { if (live) setDupMsg(r.data.duplicate ? r.data.message : ""); }).catch(() => {});
    return () => { live = false; };
  }, [form.mobile, form.whatsapp, editId, open]);
  const save = async () => {
    if (dupMsg) { toast.error(dupMsg); return; }
    if (dutyInvalid) {
      toast.error("Duty End time must be later than Duty Start time"); return;
    }
    const mi = monthInfo(form);
    const payload = {
      ...form,
      monthly_salary: Number(form.monthly_salary),
      standard_monthly_minutes: mi.stdMin,
      overtime_rate: form.overtime_rate === "" ? null : Number(form.overtime_rate),
    };
    try {
      if (editId) await api.put(`/staff/${editId}`, payload);
      else await api.post("/staff", payload);
      toast.success("Staff saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const mi = monthInfo(form);
  const perMin = mi.stdMin ? (form.monthly_salary / mi.stdMin).toFixed(4) : 0;
  const visRemoved = removed.filter((s) => !branchParam || s.branch_id === branchParam);

  return (
    <div>
      <PageHeader title="Staff Management" subtitle="Team, salary profile & per-minute rate"
        actions={canAdd ? <Button onClick={openNew} data-testid="add-staff-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]"><Plus size={16} className="mr-1" /> Add Staff</Button> : null} />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="border-primary text-primary" data-testid="branch-filter-chip">
          Showing: {selected && selected !== "all" ? branchName(selected) : "All Branches"}
        </Badge>
        <button type="button" onClick={() => setAwayOnly((v) => !v)} data-testid="staff-away-filter"
          className={`text-xs px-3 py-1 rounded-full border ${awayOnly ? "bg-amber-500/20 text-amber-300 border-amber-600" : "bg-secondary text-muted-foreground border-border"}`}>
          {awayOnly ? "✓ " : ""}Currently Away ({staff.filter((s) => s.is_away).length})
        </button>
        {isOwner && bulkSel.size > 0 && (
          <div className="flex items-center gap-2 ml-auto" data-testid="staff-bulk-bar">
            <span className="text-xs text-muted-foreground">{bulkSel.size} selected</span>
            <Button size="sm" variant="outline" className="rounded-sm h-8" data-testid="staff-bulk-show" onClick={() => bulkVisibility(true)}>Show in booking</Button>
            <Button size="sm" variant="outline" className="rounded-sm h-8" data-testid="staff-bulk-hide" onClick={() => bulkVisibility(false)}>Hide from booking</Button>
            <Button size="sm" variant="ghost" className="rounded-sm h-8" data-testid="staff-bulk-clear" onClick={() => setBulkSel(new Set())}>Clear</Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {staff.filter((s) => !awayOnly || s.is_away).map((s) => (
          <Card key={s.id} className="p-5 bg-card border-border" data-testid={`staff-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                {s.photo ? <img src={photoUrl(s.photo)} alt={s.name} className="h-12 w-12 rounded-full object-cover" /> : <div className="h-12 w-12 rounded-full bg-primary/20 text-primary flex items-center justify-center font-display text-xl">{s.name[0]}</div>}
                <div>
                  <p className="font-medium">{s.name}</p>
                  <p className="text-xs text-primary">{s.designation}</p>
                  {s.show_in_public === false && <span className="inline-block text-[10px] mt-0.5 px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border" data-testid={`staff-hidden-badge-${s.id}`}>Hidden from booking</span>}
                  <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`staff-branch-${s.id}`}>
                    {branchName(s.branch_id)}
                    {s.is_away && <span className="text-amber-400"> · Away from home ({branchName(s.home_branch_id)})</span>}
                  </p>
                  {s.is_away && awayInfo[s.id] && (
                    <p className="text-[11px] text-amber-300/90 mt-0.5" data-testid={`staff-away-info-${s.id}`}>
                      Away since {awayInfo[s.id].away_since} · Business here {rupee(awayInfo[s.id].business || 0)} · Incentive {rupee(awayInfo[s.id].incentive || 0)}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {isOwner && <input type="checkbox" checked={bulkSel.has(s.id)} onChange={() => setBulkSel((p) => { const n = new Set(p); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n; })} data-testid={`staff-bulk-check-${s.id}`} title="Select for bulk booking toggle" className="mr-1" />}
                {canEdit && (
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`}><Pencil size={14} /></Button>
                )}
                {isOwner && (
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" title="Remove staff" onClick={() => setDelTarget(s)} data-testid={`remove-staff-${s.id}`}><Trash2 size={14} /></Button>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">{s.specialization || "—"}</p>
            <div className="flex items-center gap-1 mt-2 text-primary text-sm">
              <Star size={14} fill="#C5A059" /> {s.rating || 0} <span className="text-muted-foreground">({s.review_count || 0})</span>
            </div>
            <div className="mt-4 pt-3 border-t border-border grid grid-cols-2 gap-2 text-sm">
              {canSeeFull ? (
                <>
                  <div><span className="text-muted-foreground text-xs">Salary</span><p>{rupee(s.monthly_salary)}</p></div>
                  <div><span className="text-muted-foreground text-xs">Per-minute</span><p className="text-primary">₹{s.per_minute_rate}</p></div>
                  <div><span className="text-muted-foreground text-xs">Std minutes</span><p>{s.standard_monthly_minutes}</p></div>
                  <div><span className="text-muted-foreground text-xs">Weekly off</span><p>{s.weekly_off}</p></div>
                </>
              ) : (
                <div><span className="text-muted-foreground text-xs">Weekly off</span><p>{s.weekly_off || "—"}</p></div>
              )}
            </div>
            {isOwner && (
              <div className="mt-3 pt-3 border-t border-border">
                <StaffTransfer s={s} branches={branches} branchName={branchName}
                  onTemp={moveBranch} onPerm={permanentTransfer} onHome={moveHome} />
              </div>
            )}
          </Card>
        ))}
      </div>

      {visRemoved.length > 0 && (
        <div className="mt-8">
          <button onClick={() => setShowRemoved((v) => !v)} data-testid="toggle-removed-staff"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            {showRemoved ? <ChevronDown size={16} /> : <ChevronRight size={16} />} Removed staff ({visRemoved.length})
          </button>
          {showRemoved && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3" data-testid="removed-staff-list">
              {visRemoved.map((s) => (
                <Card key={s.id} className="p-4 bg-card border-border opacity-80" data-testid={`removed-staff-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {s.photo ? <img src={photoUrl(s.photo)} alt={s.name} className="h-10 w-10 rounded-full object-cover grayscale" /> : <div className="h-10 w-10 rounded-full bg-secondary text-muted-foreground flex items-center justify-center font-display">{s.name[0]}</div>}
                      <div><p className="font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.designation}</p></div>
                    </div>
                    <Button size="sm" variant="outline" className="rounded-sm border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                      onClick={() => restore(s)} data-testid={`restore-staff-${s.id}`}><RotateCcw size={14} className="mr-1" /> Reactivate</Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit" : "Add"} Staff</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {editId ? (
              <div className="flex items-center gap-4">
                {form.photo ? <img src={photoUrl(form.photo)} alt="" className="h-16 w-16 rounded-full object-cover" /> : <div className="h-16 w-16 rounded-full bg-primary/20 text-primary flex items-center justify-center font-display text-2xl">{(form.name || "?")[0]}</div>}
                <label className="text-sm text-primary cursor-pointer flex items-center gap-1">
                  <Camera size={14} /><span className="underline">Upload / change photo</span>
                  <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="staff-photo-input" />
                </label>
              </div>
            ) : <p className="text-xs text-muted-foreground">Tip: save the staff first, then re-open to add a photo.</p>}
            <Field label="Name"><Input data-testid="staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-secondary" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Designation"><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="bg-secondary" /></Field>
              <Field label="Branch">
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger className="bg-secondary" data-testid="staff-branch"><SelectValue placeholder="Branch" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Specialization"><Input value={form.specialization} onChange={(e) => setForm({ ...form, specialization: e.target.value })} className="bg-secondary" /></Field>

            <div className="border border-border rounded-sm p-3" data-testid="staff-services-map">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium">Services This Staff Can Perform</p>
                <span className="text-xs text-muted-foreground" data-testid="staff-services-count">{(form.services || []).length} selected</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Used for Online Booking &amp; Freedom Package staff filtering. Does not restrict Billing/POS staff selection.</p>
              <div className="max-h-64 overflow-y-auto space-y-3 pr-1">
                {Object.entries((allServices || []).reduce((acc, s) => { (acc[s.category || "Other"] = acc[s.category || "Other"] || []).push(s); return acc; }, {})).map(([cat, svcs]) => {
                  const ids = svcs.map((s) => s.id);
                  const allOn = ids.every((id) => (form.services || []).includes(id));
                  return (
                    <div key={cat} data-testid={`staff-svc-cat-${cat.replace(/\W+/g, "-")}`}>
                      <label className="flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wide cursor-pointer">
                        <input type="checkbox" checked={allOn} onChange={() => toggleCat(ids, allOn)} className="accent-[#C9A24B]" data-testid={`staff-svc-cat-all-${cat.replace(/\W+/g, "-")}`} />
                        {cat}
                      </label>
                      <div className="grid grid-cols-2 gap-1.5 mt-1.5 pl-1">
                        {svcs.map((s) => (
                          <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={(form.services || []).includes(s.id)} onChange={() => toggleSvc(s.id)} className="accent-[#C9A24B]" data-testid={`staff-svc-${s.id}`} />
                            <span className="truncate">{s.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {!allServices.length && <p className="text-sm text-muted-foreground">No services found.</p>}
              </div>
            </div>
            <Field label="Incentive Designation (for incentive calculation only)">
              <Select value={form.incentive_designation || "__none__"} onValueChange={(v) => setForm({ ...form, incentive_designation: v === "__none__" ? "" : v })}>
                <SelectTrigger className="bg-secondary" data-testid="staff-incentive-designation"><SelectValue placeholder="Not assigned (uses All Designations)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not assigned (uses All Designations)</SelectItem>
                  {incDesigs.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Mobile"><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="bg-secondary" data-testid="staff-mobile" />
                {dupMsg && <p className="text-xs text-destructive mt-1" data-testid="staff-duplicate-error">{dupMsg}</p>}</Field>
              <Field label="Biometric ID"><Input value={form.biometric_id} onChange={(e) => setForm({ ...form, biometric_id: e.target.value })} className="bg-secondary" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Monthly Salary (₹)"><Input type="number" data-testid="staff-salary" value={form.monthly_salary} onChange={(e) => setForm({ ...form, monthly_salary: e.target.value })} className="bg-secondary" /></Field>
              <Field label="Overtime Rate (₹/min, optional)"><Input type="number" value={form.overtime_rate} onChange={(e) => setForm({ ...form, overtime_rate: e.target.value })} className="bg-secondary" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Duty Start"><TimePicker12 value={form.work_start} onChange={(v) => setForm({ ...form, work_start: v })} testid="staff-work-start" /></Field>
              <Field label="Duty End"><TimePicker12 value={form.work_end} onChange={(v) => setForm({ ...form, work_end: v })} testid="staff-work-end" />
                {dutyInvalid && <p className="text-xs text-destructive mt-1" data-testid="staff-duty-error">Duty End must be later than Duty Start</p>}</Field>
            </div>
            <Field label="Weekly Off">
              <Select value={form.weekly_off} onValueChange={(v) => setForm({ ...form, weekly_off: v })}>
                <SelectTrigger className="bg-secondary" data-testid="staff-weekly-off"><SelectValue /></SelectTrigger>
                <SelectContent>{WEEKDAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="bg-secondary p-3 rounded-sm text-xs text-muted-foreground" data-testid="staff-std-min-computed">
              Standard working minutes auto-calculate per calendar month — no fixed value is used.
              <span className="block text-foreground mt-1">
                This month: <b>{mi.stdMin.toLocaleString()} min</b> = {(mi.daily / 60).toFixed(2)} hrs/day ({mi.daily} min) × {mi.workingDays} working days
                {form.weekly_off ? ` (${mi.days} days − ${mi.offs} ${form.weekly_off}${mi.offs === 1 ? "" : "s"})` : ""}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Experience"><Input value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} placeholder="e.g. 5 years" className="bg-secondary" data-testid="staff-experience" /></Field>
              <Field label="WhatsApp Number"><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="Staff WhatsApp no." className="bg-secondary" data-testid="staff-whatsapp" /></Field>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer bg-secondary rounded-sm px-3 py-2">
              <input type="checkbox" checked={form.whatsapp_notify} onChange={(e) => setForm({ ...form, whatsapp_notify: e.target.checked })} data-testid="staff-wa-notify" />
              <Bell size={14} className="text-primary" /> Send WhatsApp appointment alerts to this staff
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer bg-secondary rounded-sm px-3 py-2">
              <input type="checkbox" checked={form.show_in_public !== false} onChange={(e) => setForm({ ...form, show_in_public: e.target.checked })} data-testid="staff-show-in-public" />
              <Bell size={14} className="text-primary" /> Show in Online Booking — appears in public booking (Book by Staff &amp; Book by Time) when ON. OFF keeps the staff fully active in Billing, Attendance, Salary &amp; reports.
            </label>
            {editId && (
              <div className="bg-secondary rounded-sm px-3 py-2 space-y-2" data-testid="staff-daily-report">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!form.daily_report_enabled}
                    onChange={async (e) => { const v = e.target.checked; setForm({ ...form, daily_report_enabled: v }); try { await api.put(`/reports/staff/${editId}/toggle`, { enabled: v }); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } }}
                    data-testid="staff-daily-report-toggle" />
                  <Bell size={14} className="text-primary" /> Send this staff a daily WhatsApp business report after their duty end time
                </label>
                <Button type="button" size="sm" variant="outline" className="rounded-sm" data-testid="staff-daily-report-test"
                  onClick={async () => { try { const { data } = await api.post(`/reports/staff/${editId}/test`); data.ok ? toast.success(`Test report sent to ${data.sent_to}`) : toast.error(data.message || "Could not send"); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } }}>
                  Send Test Report
                </Button>
              </div>
            )}
            <div className="bg-secondary p-3 rounded-sm text-sm flex justify-between">
              <span className="text-muted-foreground">Computed per-minute rate</span>
              <span className="text-primary font-medium" data-testid="computed-rate">₹{perMin}</span>
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={dutyInvalid || Boolean(dupMsg)} data-testid="staff-save" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o) { setDelTarget(null); setDelReason(""); } }}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Remove Staff</DialogTitle></DialogHeader>
          {delTarget && <p className="text-sm text-muted-foreground">Remove <span className="text-foreground font-medium">{delTarget.name}</span> from the team? They'll no longer appear in staff, appointments or booking. Past salary & billing records are kept.</p>}
          <Input value={delReason} onChange={(e) => setDelReason(e.target.value)} placeholder="Reason for removal (saved in Audit Log)" className="bg-secondary mt-1" data-testid="remove-staff-reason" />
          <DialogFooter>
            <Button variant="ghost" className="rounded-sm" onClick={() => { setDelTarget(null); setDelReason(""); }} data-testid="remove-staff-cancel">Cancel</Button>
            <Button className="rounded-sm bg-destructive text-white hover:bg-destructive/90" disabled={!delReason.trim()} onClick={confirmDelete} data-testid="remove-staff-confirm"><Trash2 size={15} className="mr-1" /> Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
}

function TimePicker12({ value, onChange, testid }) {
  const parse = (v) => {
    if (!v || !String(v).includes(":")) return { h: "", m: "00", p: "AM" };
    let [H, M] = String(v).split(":").map(Number);
    const p = H >= 12 ? "PM" : "AM";
    const h = (H % 12) || 12;
    return { h: String(h), m: String(M || 0).padStart(2, "0"), p };
  };
  const { h, m, p } = parse(value);
  const emit = (nh, nm, np) => {
    if (!nh) { onChange(""); return; }
    let H = Number(nh) % 12;
    if (np === "PM") H += 12;
    onChange(`${String(H).padStart(2, "0")}:${nm}`);
  };
  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const mins = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
  return (
    <div className="flex gap-2">
      <Select value={h} onValueChange={(v) => emit(v, m, p)}>
        <SelectTrigger className="bg-secondary" data-testid={`${testid}-hour`}><SelectValue placeholder="HH" /></SelectTrigger>
        <SelectContent>{hours.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={m} onValueChange={(v) => emit(h, v, p)}>
        <SelectTrigger className="bg-secondary" data-testid={`${testid}-min`}><SelectValue placeholder="MM" /></SelectTrigger>
        <SelectContent>{mins.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={p} onValueChange={(v) => emit(h, m, v)}>
        <SelectTrigger className="bg-secondary w-20" data-testid={`${testid}-ampm`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="AM">AM</SelectItem><SelectItem value="PM">PM</SelectItem></SelectContent>
      </Select>
    </div>
  );
}
